/**
 * Utilities for number formatting and rounding.
 */

import { PERCENT_FORMAT, PERCENTAGE_MULTIPLIER } from 'constants/general';
import { LanguageCode } from 'enums';

interface FormatPercentOptions {
  readonly locale?: string;
  readonly maximumFractionDigits?: number;
  readonly minimumFractionDigits?: number;
}

export const formatPercent = (
  percent: number,
  {
    locale = LanguageCode.EN,
    maximumFractionDigits = PERCENT_FORMAT.DEFAULT_FRACTION_DIGITS,
    minimumFractionDigits = PERCENT_FORMAT.DEFAULT_FRACTION_DIGITS
  }: FormatPercentOptions = {}
): string =>
  new Intl.NumberFormat(locale, {
    style: 'percent',
    maximumFractionDigits,
    minimumFractionDigits
  }).format(percent / PERCENTAGE_MULTIPLIER);

export function roundToDecimals(
  value: number | null | undefined,
  decimals = 1
): number {
  if (value === null || value === undefined) {
    return 0;
  }
  const num = Number(value);
  if (Number.isNaN(num)) {
    return 0;
  }
  if (num === 0) {
    return 0;
  }
  const factor = Math.pow(10, decimals);
  const sign = num < 0 ? -1 : 1;
  return (Math.round(Math.abs(num) * factor) / factor) * sign;
}
export function parseIntOrZero(value: string, radix = 10): number {
  const parsed = parseInt(value, radix);
  return isNaN(parsed) ? 0 : parsed;
}

export const isNumber = (value: unknown): value is number =>
  typeof value === 'number';

export function isEven(value: number): boolean {
  return value % 2 === 0;
}

export function isOdd(value: number): boolean {
  return value % 2 !== 0;
}

export const clamp = (value: number, min: number, max: number): number =>
  Math.max(min, Math.min(max, value));

export const clampRounded = (value: number, min: number, max: number): number =>
  clamp(Math.round(value), min, max);

export const isIntegerInBounds = (
  raw: string,
  bounds: { readonly MIN: number; readonly MAX: number }
): boolean => {
  const trimmed = raw.trim();
  if (trimmed.length === 0) {
    return false;
  }
  const value = Number(trimmed);
  if (!Number.isInteger(value)) {
    return false;
  }
  return value >= bounds.MIN && value <= bounds.MAX;
};

export const ratioToPercent = (part: number, total: number): number =>
  (Number(part) / total) * 100;

/** Convert a 0–1 unit interval to a whole percentage (0–100). */
export const unitIntervalToPercent = (value: number): number =>
  Math.round(clamp(value, 0, 1) * 100);

export function formatToFixedDecimals(
  value: number | null | undefined,
  decimals = 1
): string | undefined {
  if (value === null || value === undefined || Number.isNaN(Number(value))) {
    return undefined;
  }
  return roundToDecimals(Number(value), decimals).toFixed(decimals);
}

export default {
  roundToDecimals,
  parseIntOrZero,
  isNumber,
  isEven,
  isOdd,
  clamp,
  clampRounded,
  ratioToPercent,
  unitIntervalToPercent,
  formatToFixedDecimals,
  formatPercent
};
